import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { reducerType } from 'reducer';
import useGetDestination from './useGetDestination';
import useShowGrades from './useShowGrades';
import { contentInfoType } from 'types';
import { makeStyles } from 'makeStyles';
import { colors } from 'utils/colors';

/* ---------- helpers ---------- */
const isAbs = (s?: string) => !!s && /^https?:\/\//i.test(String(s).trim());

/** שולף image=... מתוך מחרוזת URL (בטוח גם כשהיא יחסית) */
function pickImageParam(raw?: string): string {
  const s = (raw ?? '').toString().trim();
  if (!s) return '';
  try {
    const u = isAbs(s) ? new URL(s) : new URL(s, window.location.origin);
    const v = u.searchParams.get('image');
    return v ? decodeURIComponent(v) : '';
  } catch {
    const q = s.split('?')[1] || '';
    const m = q.match(/(?:^|&)image=([^&]+)/i);
    return m ? decodeURIComponent(m[1]) : '';
  }
}

/** מחלצים imageKey לפי סדר עדיפויות:
 * img_id → image מתוך lesson_id/app → image שדה ישיר → lessontype → lesson_id
 * מסירים סיומות קובץ אם קיימות.
 */
function extractImageKey(val: any): string {
  const cands = [
    val?.img_id,
    pickImageParam(val?.lesson_id),
    pickImageParam(val?.app),
    val?.image,
    val?.lessontype,
    val?.lesson_id,
  ]
    .map(x => (x ?? '').toString().trim())
    .filter(Boolean);

  let key = cands[0] || '';
  key = key.replace(/\.(jpg|jpeg|png|webp)$/i, '');

  console.log('[IMG][grid] key="%s" from=', key, {
    img_id: val?.img_id,
    lesson_id: val?.lesson_id,
    app: val?.app,
    image: val?.image,
    lessontype: val?.lessontype,
  });

  return key;
}

/** בונים URL לתמונה (תמיד apps3 + JPG, כפי שביקשת) */
function buildImageUrl(key: string): string {
  const url = `https://www.yisumatica.org.il/flash-upload/js/img/apps3/${key}.JPG`;
  console.log('[IMG][grid] url=', url);
  return url;
}

const PLACEHOLDER = '/images/yisumaticaLogo.png';

/* ---------- styles ---------- */
const useStyles = makeStyles()(() => ({
  imageContainer: {
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    flexFlow: 'column',
    textDecoration: 'none',
  },
  image: {
    height: '60%',
    width: '90%',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  },
  name: {
    height: '20%',
    width: '90%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexFlow: 'column',
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.darkText,
  },
  grades: {
    height: '10%',
    width: '90%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexFlow: 'column',
    textAlign: 'center',
    borderBottom: 10,
  },
  showApproved: {
    border: '1px solid #00F',
    cursor: 'pointer',
  },
}));

/* ---------- component ---------- */
type LinkToAppProps = {
  appName: string;
  lang: string;
  path: string;
  guest: boolean;
  disabled: boolean;
  val: contentInfoType;
};

const LinkToApp: React.FC<LinkToAppProps> = ({
  appName,
  lang,
  path,
  guest,
  disabled,
  val,
}) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const { grades, showGrades } = useShowGrades(val, lang);
  const destination = useGetDestination(val, guest, disabled);

  const [imageStyle, gradeStyle] = useMemo(() => {
    const key = extractImageKey(val);
    const imgUrl = key ? buildImageUrl(key) : PLACEHOLDER;

    return [
      {
        backgroundImage: `url("${imgUrl}")`,
        height: showGrades ? (lang === 'heb' ? '45%' : '35%') : '60%',
      } as React.CSSProperties,
      {
        fontWeight: lang === 'heb' ? 'bold' : undefined,
        fontSize: lang === 'heb' ? undefined : 14,
      } as React.CSSProperties,
    ];
  }, [val, showGrades, lang]);

  return (
    <div
      className={classes.imageContainer}
      onClick={() => {
        console.log('[GUEST CLICK] LinkToApp onClick:', {
          destination,
          guest,
          disabled,
          appId: val.uid || val.lesson_id,
          appName: val.app || val.lesson_id,
          totalLikes: val.totalLikes
        });
        if (destination === 'appPage') {
          // navigate(path)  // אם תרצה לחזור לנתיב המלא
          navigate('new');
        }
        import('functions/actions/select_content').then(func => {
          console.log('[GUEST CLICK] Calling selectContent with destination:', destination);
          func.selectContent(val, 'pages/apps/grid/LinkToApp', destination);
        });
      }}
    >
      <div className={classes.image} style={imageStyle} />
      <div className={classes.name}>{appName}</div>
      {showGrades && (
        <div className={classes.grades} style={gradeStyle}>
          {grades}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (reducer: reducerType) => ({
  lang: reducer.components.lang,
});

export default connect(mapStateToProps)(LinkToApp);
